import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormBuilder, AbstractControl, ValidationErrors, FormControl } from '@angular/forms';
import { DataService } from '../services/data.service';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, switchMapTo, tap, map } from 'rxjs/operators';
import { AppStore } from '../../shared/store/lookup-store.service';
import { Account } from '../models/account';
import { AuthenticationService } from '../../auth/services/authentication.service';
import { ModalConfigHelper } from '../../shared/helpers/modal-config-helper';
import { Credentials, ExternalCredentials } from '../../auth/models/credentials';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountTypes } from '../../shared/models/AccountTypes';
import { AppModalComponent } from '../../shared/components/app-modal/app-modal.component';
import { ModalConfig } from '../../shared/models/modal.config';

@Component({
  selector: 'app-signup-form',
  templateUrl: './signup-form.component.html',
  styleUrls: ['./signup-form.component.scss']
})
export class SignupFormComponent implements OnInit {
  @ViewChild("messageModal") private modalComponent: AppModalComponent;
  modalConfig: ModalConfig;

  signInTypeErrorMsg = "Please select above signup as Candidate or Job Poster.";
  returnUrl: string;
  signupType: string = "";
  signupTypeStyles: string[] = [];
  form: FormGroup;
  signupError: string = "";

  constructor(private formBuilder: FormBuilder,
    public dataService: DataService,
    public authenticationService: AuthenticationService,
    public lookupStore: AppStore,
    public route: ActivatedRoute,
    public router: Router) {

    this.modalConfig = ModalConfigHelper.getEmailVerificationModalConfig(this.onDismiss, this.onClose);
    this.authenticationService.clearAuthenticationToken();
    this.signupTypeStyles[0] = "";
    this.signupTypeStyles[1] = "";
  }

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "/";
    this.form = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]],
      passwordGroup: this.formBuilder.group({
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
      }, { validator: this.checkPasswords }),
      acceptedTerms: [false, [Validators.pattern('true')]],
      signupType: [this.signupType, Validators.required],
    });
  }

  signupTypeClicked(signupType: string): void {
    this.signupError = "";
    this.signupType = signupType;
    this.form.patchValue({ "signupType": this.signupType });
    switch (this.signupType) {
      case "employer": {
        this.signupTypeStyles[0] = "";
        this.signupTypeStyles[1] = "active";
        break;
      }
      case "candidate": {
        this.signupTypeStyles[1] = "";
        this.signupTypeStyles[0] = "active";
        break;
      }
    }
  }

  signup(): boolean {
    if(!this.signupType) {
      this.signupError = this.signInTypeErrorMsg;
      return false;
    }
    if (!this.form.valid) {
      return false;
    }

    const account = Account.parse(this.form);
    this.authenticationService.fireSignUp(account.email, account.password).then(() => {
      this.modalComponent.open();
    }).catch(e => {
      this.signupError = e;
    });
  }

  onDismissError() {
    this.signupError = "";
    this.form.reset();
  }

  checkIfUserNameExists() {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.valueChanges || control.pristine) {
        return of(null);
      } else {
        return control.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          take(1),
          switchMapTo(this.dataService.checkUserNameExists(control.value.trim())),
          tap(() => control.markAsTouched),
          map((data) => (data ? {
            userExist: true
          } : null))
        );
      }
    }
  }

  checkPasswords(group: FormGroup) { // here we have the 'passwords' group
    let pass = group.get('password').value;
    let confirmPass = group.get('confirmPassword').value;

    return pass === confirmPass ? null : { notSame: true }
  }

  googleSignIn(): void {
    if(!this.signupType) {
      this.signupError = this.signInTypeErrorMsg;
      return;
    }
    const accountType = this.signupType === "employer" ? AccountTypes.Employer : AccountTypes.Candidate;
    this.authenticationService.gmailSignIn(accountType).then(s => this.redirectAfterLogin());
  }

  private redirectAfterLogin(): void {
    switch (this.signupType) {
      case "employer": {
        this.router.navigate(['/employer']);
        break;
      }
      case "candidate": {
        this.router.navigate(['/candidate']);
        break;
      }
    }
  }

  onClose = (): Observable<boolean> => {
    this.router.navigate(['/signin']);
    return of<boolean>(true);
  };

  onDismiss = (): Observable<boolean> => {
    return of<boolean>(true);
  };

}
